import { styled } from 'dripsy';
import React from 'react';
import { ModalComponentProp } from 'react-native-modalfy';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { observer } from 'mobx-react-lite';

import { ModalStackParamsType } from '../../modals/types';
import { ModalView } from './components/ModalView';
import { BaseText } from '../common/BaseText';
import { Separator } from '../common/Separator';
import { IconTouchable } from '../common/IconTouchable';
import { AnalyticsService, ToastService } from '@services';
import { parseApiError } from '@utils';
import { TrackFeedbackForm } from '@components/form/TrackFeedbackForm';
import { TrackFeedbackFormDataType } from '@validation';
import { useTracksModel } from '@models/Tracks/TracksModel';
import { TrackFeedbackSubmitOptionEnum } from '@api';

const ModalViewContainer = styled(ModalView)({
  py: '$8',
  px: '$8',
  width: 391,
});

const TitleContainer = styled(View)({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const Title = styled(BaseText)({
  variant: 'text.xlarge',
});

export interface ITrackFeedbackFormModalParams {
  feedbackOptionType: TrackFeedbackSubmitOptionEnum | null;
  cancelAction: () => void;
}

type ModalPropsType = ModalComponentProp<
  ModalStackParamsType,
  void,
  'TrackFeedbackFormModal'
>;

export const TrackFeedbackFormModal = observer(
  ({ modal }: ModalPropsType) => {
    const { t } = useTranslation();
    const track = useTracksModel();
    const cancelAction = modal.getParam('cancelAction');
    const feedbackOptionType = modal.getParam('feedbackOptionType');

    function closeModal() {
      modal.closeModal();
      cancelAction();
    }

    async function submitFeedback(
      formValues: TrackFeedbackFormDataType,
    ) {
      try {
        await track.feedback.sendTrackFeedback.run({
          feedbackOptionType,
          ...formValues,
        });

        ToastService.showSuccess(t('trackFeedback.successMessage'));
        AnalyticsService.trackSubmitFeedback();
        closeModal();
      } catch (error) {
        ToastService.showError(parseApiError(error).message);
      }
    }

    return (
      <ModalViewContainer>
        <TitleContainer>
          <Title>{t('common.trackFeedbackForm.title')}</Title>

          <IconTouchable
            onPress={closeModal}
            name="close-light"
            size={24}
          />
        </TitleContainer>

        <Separator />

        <TrackFeedbackForm
          isFormSubmitting={
            track.feedback.sendTrackFeedback.inProgress
          }
          onCancel={closeModal}
          onSubmit={submitFeedback}
        />
      </ModalViewContainer>
    );
  },
);
